// client/src/pages/admin/StockPerformance.js
// What's selling, what isn't, and what to buy more of.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { formatCurrency } from '../../utils/currency';
import { API_BASE_URL } from '../../utils/media';

const STATUSES = {
  buy_now: { label: 'Buy more now', color: 'error', group: 'buy', help: 'Sold out, and people have been buying it.' },
  buy_soon: { label: 'Buy more soon', color: 'warning', group: 'buy', help: 'At the current rate, stock runs out within 30 days.' },
  selling_well: { label: 'Selling well', color: 'success', group: 'selling', help: 'Selling, with one to three months of stock left.' },
  selling_slowly: { label: 'Selling slowly', color: 'default', group: 'selling', help: "Selling, but there's more than three months of stock." },
  not_selling: { label: 'Not selling', color: 'default', group: 'not', help: 'In stock, with no paid sales in this period.' },
  sold_out_quiet: { label: 'Sold out, no recent sales', color: 'default', group: 'not', help: 'No stock, and no paid sales in this period.' },
  too_new: { label: 'Too new to tell', color: 'info', group: 'new', help: 'Added less than two weeks ago, with no sales yet.' },
};

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'buy', label: 'Buy more' },
  { value: 'selling', label: 'Selling' },
  { value: 'not', label: 'Not selling' },
];

const lastsLabel = (row) => {
  if (row.stock === 0) return 'Sold out';
  if (row.daysOfStock === null) return '—';
  if (row.daysOfStock > 365) return 'Over a year';
  if (row.daysOfStock < 1) return 'Under a day';
  return `About ${row.daysOfStock} day${row.daysOfStock === 1 ? '' : 's'}`;
};

// A leading = + - or @ would make a spreadsheet treat text as a formula.
const csvCell = (value) => {
  const text = String(value ?? '');
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

const StockPerformance = ({ days, token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setError('');
    fetch(`${API_BASE_URL}/api/admin/reports/stock-performance?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Could not load the stock report.'))))
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [token, days]);

  const counts = useMemo(() => {
    const totals = { all: 0, buy: 0, selling: 0, not: 0 };
    (data?.products || []).forEach((row) => {
      totals.all += 1;
      const { group } = STATUSES[row.status];
      if (totals[group] !== undefined) totals[group] += 1;
    });
    return totals;
  }, [data]);

  const shown = useMemo(() => (data?.products || []).filter((row) => filter === 'all' || STATUSES[row.status].group === filter), [data, filter]);

  if (error) return <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>;
  if (!data) return <CircularProgress size={28} sx={{ mb: 4 }} />;

  const maxUnits = Math.max(...data.products.map((row) => row.units), 0);

  const exportCsv = () => {
    const header = ['Product', 'Category', 'Status', `Units sold (${data.days} days)`, 'Revenue', 'In stock', 'Stock lasts', 'Last sold', 'Suggested order'];
    const rows = shown.map((row) => [
      row.name, row.category, STATUSES[row.status].label, row.units, row.revenue.toFixed(2), row.stock, lastsLabel(row),
      row.lastSold ? new Date(row.lastSold).toLocaleDateString() : 'Never', row.suggestedOrder || '',
    ]);
    const url = URL.createObjectURL(new Blob([[header, ...rows].map((row) => row.map(csvCell).join(',')).join('\n')], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `stock-report-${data.days}-days.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 0.5 }}>
        <Typography variant="h6" component="h2">What's selling and what to buy</Typography>
        <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={exportCsv} disabled={shown.length === 0}>Export this list</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Based on paid orders in the last {data.days === 365 ? 'year' : `${data.days} days`}. Suggested amounts aim for about {data.targetDays} days of stock at the current rate. Treat them as a guide, and allow for how long your supplier takes.
      </Typography>

      <ToggleButtonGroup exclusive size="small" value={filter} onChange={(e, next) => next && setFilter(next)} aria-label="Filter products" sx={{ mb: 2, flexWrap: 'wrap' }}>
        {FILTERS.map(({ value, label }) => (
          <ToggleButton key={value} value={value} sx={{ textTransform: 'none' }}>{label} ({counts[value]})</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Nothing in this group right now.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell><TableCell>Status</TableCell><TableCell>Units sold</TableCell>
                <TableCell align="right">In stock</TableCell><TableCell>Stock lasts</TableCell><TableCell>Last sold</TableCell>
                <TableCell align="right">Suggested order</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((row) => {
                const status = STATUSES[row.status];
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Link to={`/products/${row.id}`}>{row.name}</Link>
                      {row.category && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{row.category}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={status.help}><Chip size="small" label={status.label} color={status.color} variant={status.color === 'default' ? 'outlined' : 'filled'} /></Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`${row.units} sold, ${formatCurrency(row.revenue)}`}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 120 }}>
                          <Box sx={{ flexGrow: 1, height: 10, bgcolor: 'action.hover' }}>
                            <Box sx={{ height: '100%', width: `${maxUnits > 0 && row.units > 0 ? Math.max((row.units / maxUnits) * 100, 2) : 0}%`, bgcolor: 'primary.main' }} />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.units}</Typography>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.stock}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{lastsLabel(row)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.lastSold ? new Date(row.lastSold).toLocaleDateString() : 'Never'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: row.suggestedOrder ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>{row.suggestedOrder || '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StockPerformance;
